import React from 'react';
import '../styles/components/StructuredRecipeDisplay.css';

/**
 * StructuredRecipeDisplay - 構造化されたレシピ表示コンポーネント
 * マークダウンテキストをパースして、見やすいセクションに分けて表示
 */
const StructuredRecipeDisplay = ({ recipeMarkdown }) => {
  // マークダウンをパースして構造化
  const parseRecipe = (markdown) => {
    const lines = markdown.split('\n');
    const recipe = {
      title: '',
      cookingTime: '',
      ingredients: [],
      steps: [],
      tips: []
    };

    let currentSection = null;

    for (let line of lines) {
      line = line.trim();

      // 料理名（## で始まる）
      if (line.startsWith('## ')) {
        recipe.title = line.replace('## ', '').trim();
        currentSection = null;
      }
      // 調理時間
      else if (line.includes('調理時間')) {
        const match = line.match(/(\d+)分/);
        if (match) {
          recipe.cookingTime = match[1];
        }
        currentSection = null;
      }
      // 材料セクション
      else if (line.startsWith('### 材料')) {
        currentSection = 'ingredients';
      }
      // 作り方セクション
      else if (line.startsWith('### 作り方')) {
        currentSection = 'steps';
      }
      // ポイントセクション
      else if (line.startsWith('### ポイント') || line.startsWith('### コツ')) {
        currentSection = 'tips';
      }
      // セクション境界
      else if (line.startsWith('###') || line === '---') {
        currentSection = null;
      }
      // コンテンツ
      else if (line && currentSection) {
        // 箇条書き（- で始まる）
        if (line.startsWith('- ')) {
          const content = line.replace('- ', '').trim();
          if (currentSection === 'ingredients') {
            recipe.ingredients.push(content);
          }
        }
        // 番号付きリスト（1. で始まる）
        else if (/^\d+\.\s/.test(line)) {
          const content = line.replace(/^\d+\.\s/, '').trim();
          if (currentSection === 'steps') {
            recipe.steps.push(content);
          }
        }
        // その他のテキスト（ポイントなど）
        else if (currentSection === 'tips' && line.length > 0) {
          recipe.tips.push(line);
        }
      }
    }

    return recipe;
  };

  const recipe = parseRecipe(recipeMarkdown);

  return (
    <div className="structured-recipe">
      {/* 料理名 */}
      <div className="recipe-header">
        <h2 className="recipe-title">{recipe.title}</h2>
        {recipe.cookingTime && (
          <div className="recipe-time">
            <span className="time-icon">⏱️</span>
            <span className="time-text">{recipe.cookingTime}分</span>
          </div>
        )}
      </div>

      {/* 材料セクション */}
      {recipe.ingredients.length > 0 && (
        <div className="recipe-section recipe-section--ingredients">
          <h3 className="section-title">
            <span className="section-icon">🥘</span>
            材料
          </h3>
          <div className="ingredients-grid">
            {recipe.ingredients.map((ingredient, index) => {
              // 「食材名: 分量」の形式を分割
              const parts = ingredient.split(/[：:]/);
              const name = parts[0]?.trim() || ingredient;
              const amount = parts[1]?.trim() || '';

              return (
                <div key={index} className="ingredient-item">
                  <span className="ingredient-name">{name}</span>
                  {amount && <span className="ingredient-amount">{amount}</span>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* 作り方セクション */}
      {recipe.steps.length > 0 && (
        <div className="recipe-section recipe-section--steps">
          <h3 className="section-title">
            <span className="section-icon">👩‍🍳</span>
            作り方
          </h3>
          <ol className="steps-list">
            {recipe.steps.map((step, index) => (
              <li key={index} className="step-item">
                <div className="step-number">{index + 1}</div>
                <div className="step-content">{step}</div>
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* ポイントセクション */}
      {recipe.tips.length > 0 && (
        <div className="recipe-section recipe-section--tips">
          <h3 className="section-title">
            <span className="section-icon">💡</span>
            ポイント
          </h3>
          <div className="tips-content">
            {recipe.tips.map((tip, index) => (
              <p key={index} className="tip-text">{tip}</p>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default StructuredRecipeDisplay;
